/* FILE: frontend/src/components/CoreAndShellPillar.jsx */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function CoreAndShellPillar() {
  const { t } = useTranslation();

  // Active database slugs mapped out for structural integrity
  const categories = {
    concrete: [
      { name: t('coreAndShell.materials.readyMix'), slug: "ready-mix-concrete-delivery", hs: "25" },
      { name: t('coreAndShell.materials.portlandCement'), slug: "bulk-portland-cement-suppliers", hs: "25" },
      { name: t('coreAndShell.materials.precastPanels'), slug: "precast-concrete-wall-panels", hs: "68" },
      { name: t('coreAndShell.materials.precastSuppliers'), slug: "precast-concrete-suppliers", hs: "68" },
      { name: t('coreAndShell.materials.rebar10m'), slug: "10m-rebar-bulk-pricing", hs: "72" },
      { name: t('coreAndShell.materials.rebar15m'), slug: "15m-reinforcing-steel-bars", hs: "72" }
    ],
    steel: [
      { name: t('coreAndShell.materials.steelBeams'), slug: "structural-steel-beams", hs: "72" },
      { name: t('coreAndShell.materials.steelFabricators'), slug: "structural-steel-fabricators", hs: "73" },
      { name: t('coreAndShell.materials.steelComponents'), slug: "structural-steel-components", hs: "73" }
    ],
    timber: [
      { name: t('coreAndShell.materials.cltCommercial'), slug: "cross-laminated-timber-clt-commercial-canada", hs: "44" },
      { name: t('coreAndShell.materials.cltManufacturers'), slug: "clt-manufacturers-canada", hs: "44" },
      { name: t('coreAndShell.materials.spfWholesale'), slug: "spf-lumber-wholesale", hs: "44" },
      { name: t('coreAndShell.materials.dimensionalLumber'), slug: "dimensional-lumber-bulk-price", hs: "44" }
    ],
    envelope: [
      { name: t('coreAndShell.materials.drywall'), slug: "commercial-fire-rated-drywall", hs: "68" }
    ],
    compliance: [
      { name: t('coreAndShell.materials.materialCompliance'), slug: "structural-material-compliance", hs: "73" },
      { name: t('coreAndShell.materials.sourcingPlatforms'), slug: "structural-sourcing-platforms", hs: "73" }
    ]
  };

  return (
    <div className="bg-slate-50 min-h-screen text-slate-900 font-sans antialiased">
      {/* Hero Header Banner */}
      <header className="bg-slate-900 text-white py-16 px-6 border-b border-slate-800">
        <div className="max-w-5xl mx-auto">
          <div className="inline-block bg-blue-500/10 text-blue-400 text-xs font-semibold tracking-wider uppercase px-3 py-1 rounded-full mb-4 border border-blue-500/20">
            {t('coreAndShell.heroBadge')}
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
            {t('coreAndShell.heroTitle')}
          </h1>
          <p className="text-lg text-slate-400 max-w-3xl leading-relaxed">
            {t('coreAndShell.heroSubtitle')}
          </p>
        </div>
      </header>

      {/* Tri-Persona Information Matrix */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-blue-600 font-bold mb-1">{t('coreAndShell.personas.buyersTitle')}</div>
            <p className="text-sm text-slate-600">{t('coreAndShell.personas.buyersText')}</p>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-emerald-600 font-bold mb-1">{t('coreAndShell.personas.suppliersTitle')}</div>
            <p className="text-sm text-slate-600">{t('coreAndShell.personas.suppliersText')}</p>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-amber-600 font-bold mb-1">{t('coreAndShell.personas.complianceTitle')}</div>
            <p className="text-sm text-slate-600">{t('coreAndShell.personas.complianceText')}</p>
          </div>
        </div>

        <hr className="border-slate-200 my-10" />

        {/* Phase 1: Concrete */}
        <div className="mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="flex items-center justify-center bg-slate-200 text-slate-800 font-bold w-8 h-8 rounded-full text-sm">01</span>
            <h2 className="text-2xl font-bold tracking-tight text-slate-800">{t('coreAndShell.sections.concreteTitle')}</h2>
          </div>
          <p className="text-slate-600 mb-6 leading-relaxed">{t('coreAndShell.sections.concreteText')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {categories.concrete.map((page, i) => (
              <Link key={i} to={`/materials/${page.slug}`} className="group block bg-white p-4 rounded-lg border border-slate-200 hover:border-blue-500 shadow-sm transition-all duration-200">
                <div className="flex justify-between items-start">
                  <span className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">{page.name}</span>
                  <span className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded text-slate-500">HS {page.hs}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Phase 2: Steel */}
        <div className="mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="flex items-center justify-center bg-slate-200 text-slate-800 font-bold w-8 h-8 rounded-full text-sm">02</span>
            <h2 className="text-2xl font-bold tracking-tight text-slate-800">{t('coreAndShell.sections.steelTitle')}</h2>
          </div>
          <p className="text-slate-600 mb-6 leading-relaxed">{t('coreAndShell.sections.steelText')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {categories.steel.map((page, i) => (
              <Link key={i} to={`/materials/${page.slug}`} className="group block bg-white p-4 rounded-lg border border-slate-200 hover:border-blue-500 shadow-sm transition-all duration-200">
                <div className="flex justify-between items-start">
                  <span className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">{page.name}</span>
                  <span className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded text-slate-500">HS {page.hs}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Phase 3: Mass Timber */}
        <div className="mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="flex items-center justify-center bg-slate-200 text-slate-800 font-bold w-8 h-8 rounded-full text-sm">03</span>
            <h2 className="text-2xl font-bold tracking-tight text-slate-800">{t('coreAndShell.sections.timberTitle')}</h2>
          </div>
          <p className="text-slate-600 mb-6 leading-relaxed">{t('coreAndShell.sections.timberText')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {categories.timber.map((page, i) => (
              <Link key={i} to={`/materials/${page.slug}`} className="group block bg-white p-4 rounded-lg border border-slate-200 hover:border-blue-500 shadow-sm transition-all duration-200">
                <div className="flex justify-between items-start">
                  <span className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">{page.name}</span>
                  <span className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded text-slate-500">HS {page.hs}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Phase 4: Envelope */}
        <div className="mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="flex items-center justify-center bg-slate-200 text-slate-800 font-bold w-8 h-8 rounded-full text-sm">04</span>
            <h2 className="text-2xl font-bold tracking-tight text-slate-800">{t('coreAndShell.sections.envelopeTitle')}</h2>
          </div>
          <p className="text-slate-600 mb-6 leading-relaxed">{t('coreAndShell.sections.envelopeText')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {categories.envelope.map((page, i) => (
              <Link key={i} to={`/materials/${page.slug}`} className="group block bg-white p-4 rounded-lg border border-slate-200 hover:border-blue-500 shadow-sm transition-all duration-200">
                <div className="flex justify-between items-start">
                  <span className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">{page.name}</span>
                  <span className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded text-slate-500">HS {page.hs}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Phase 5: Compliance Infrastructure */}
        <div className="mb-14 bg-slate-900 text-white p-8 rounded-2xl shadow-md border border-slate-800">
          <h2 className="text-2xl font-bold tracking-tight mb-4">{t('coreAndShell.sections.complianceTitle')}</h2>
          <p className="text-slate-300 mb-6 leading-relaxed">{t('coreAndShell.sections.complianceText')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {categories.compliance.map((page, i) => (
              <Link key={i} to={`/materials/${page.slug}`} className="group block bg-slate-800 p-4 rounded-lg border border-slate-700 hover:border-blue-400 transition-all duration-200">
                <div className="flex justify-between items-start">
                  <span className="font-semibold text-white group-hover:text-blue-400 transition-colors">{page.name}</span>
                  <span className="text-xs font-mono bg-slate-700 px-2 py-0.5 rounded text-slate-400">HS {page.hs}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Action Portal Callout */}
        <div className="bg-blue-600 text-white p-8 rounded-2xl text-center shadow-lg shadow-blue-600/10">
          <h3 className="text-2xl font-bold mb-2">{t('coreAndShell.cta.title')}</h3>
          <p className="text-blue-100 max-w-2xl mx-auto mb-6">{t('coreAndShell.cta.subtitle')}</p>
          <Link to="/directory" className="inline-block bg-white text-blue-600 hover:bg-slate-100 font-bold px-6 py-3 rounded-xl transition shadow-sm">
          {t('coreAndShell.cta.button')}
          </Link>
        </div>
      </section>
    </div>
  );
}